import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen,
  GraduationCap,
  Layers,
  FileText,
  FileCheck2,
  Sparkles,
  Award,
  Filter,
  Plus,
  RotateCcw,
  Printer,
  Download,
  Copy,
  Archive,
  CheckCircle2,
  AlertCircle,
  Clock,
  Eye,
  EyeOff,
  Search,
  Upload,
  ChevronRight,
  ShieldCheck,
  Check,
  X,
  Edit3,
  Wand2,
  AlertTriangle,
  HelpCircle,
  Calculator,
  Languages,
  ChevronDown,
  BarChart3,
  TrendingUp,
  FileSpreadsheet,
  BadgeCheck,
  HelpCircle as QuestionIcon,
  Hash,
  FileCode,
  Sliders,
  CheckSquare,
  Square,
  ExternalLink,
  ShieldAlert,
  Percent,
  Send,
  MessageSquare,
  Inbox,
  CheckCheck,
  History,
  FileUp,
  FileDigit,
  RefreshCw,
  FolderCheck,
  UserCheck
} from "lucide-react";

export const BoardExamPrepPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";

  const getAuthToken = () => localStorage.getItem("token") || "";

  // Master States
  const [activeTab, setActiveTab] = useState<string>("dashboard");
  const [loading, setLoading] = useState<boolean>(false);
  const [boards, setBoards] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);

  // Filter States
  const [selectedBoard, setSelectedBoard] = useState<string>("2"); // CBSE default
  const [selectedClass, setSelectedClass] = useState<string>("1"); // Class 10 default
  const [selectedSubject, setSelectedSubject] = useState<string>("2"); // Math default
  const [selectedMedium, setSelectedMedium] = useState<string>("");
  const [selectedChapter, setSelectedChapter] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("");
  const [selectedVerification, setSelectedVerification] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isPyqFilter, setIsPyqFilter] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedSet, setSelectedSet] = useState<string>("");

  // Data States
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [contentStats, setContentStats] = useState<any[]>([]);
  const [pyqAnalysis, setPyqAnalysis] = useState<any>(null);
  const [qualityStats, setQualityStats] = useState<any>(null);
  const [sourceFiles, setSourceFiles] = useState<any[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [topics, setTopics] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [pyqs, setPyqs] = useState<any[]>([]);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [generatedPapers, setGeneratedPapers] = useState<any[]>([]);
  const [activePaper, setActivePaper] = useState<any>(null);

  // Solution Visibility State
  const [showSolutionMap, setShowSolutionMap] = useState<Record<number, boolean>>({});

  // Phase 16 Math Studio & Normalizer States
  const [mathSymbols, setMathSymbols] = useState<Record<string, any[]>>({});
  const [normalizerInput, setNormalizerInput] = useState<string>("Solve for x: under root 2 + sqrt(5) = 0, where x square >= 0 and pi = 3.14.");
  const [normalizerResult, setNormalizerResult] = useState<any>(null);
  const [normalizerLoading, setNormalizerLoading] = useState<boolean>(false);
  const [selectedSubtopicChapter, setSelectedSubtopicChapter] = useState<number | null>(null);
  const [subtopicsList, setSubtopicsList] = useState<any[]>([]);

  // Phase 15 Real Extraction & Staging States
  const [extractionRawText, setExtractionRawText] = useState<string>(`Section A
Question 1. The HCF of 96 and 404 is [1]
Question 2. If alpha and beta are the zeroes of the quadratic polynomial f(x) = x^2 - 5x + 6, find the value of alpha + beta. (1 mark)

Section B
Question 3. Prove that under root 5 is an irrational number. [3 marks]
Question 4. Solve the quadratic equation: 2x square - 7x + 3 = 0 [3 marks]

Section C
Question 5. State and prove the Basic Proportionality Theorem (Thales Theorem). [5 marks]`);
  const [extractionFilename, setExtractionFilename] = useState<string>("CBSE_Class10_Maths_2024_Set1.pdf");
  const [extractionYear, setExtractionYear] = useState<string>("2024");
  const [extractionSet, setExtractionSet] = useState<string>("Set-1");
  const [extractionPaperCode, setExtractionPaperCode] = useState<string>("30/1/1");
  const [extractedQuestions, setExtractedQuestions] = useState<any[]>([]);
  const [extractionSummary, setExtractionSummary] = useState<any>(null);
  const [selectedExtractedIndices, setSelectedExtractedIndices] = useState<number[]>([]);
  const [bulkChapterSelection, setBulkChapterSelection] = useState<string>("");
  const [commitMessage, setCommitMessage] = useState<string>("");

  // Phase 15 Enhancement: Teacher Submissions States
  const [teacherContentType, setTeacherContentType] = useState<string>("Question Paper");
  const [teacherTitle, setTeacherTitle] = useState<string>("");
  const [teacherFilename, setTeacherFilename] = useState<string>("Maths_Paper_Term1.docx");
  const [teacherFileType, setTeacherFileType] = useState<string>("docx");
  const [teacherRawContent, setTeacherRawContent] = useState<string>(`Section A
Question 1. Find HCF of 6 and 20 by prime factorisation. [2 marks]
Question 2. If alpha and beta are zeroes of x square - 2x - 8, find alpha + beta. [1 mark]

Section B
Question 3. Prove that under root 3 is an irrational number. [3 marks]
Question 4. Solve quadratic equation: 2x square - 5x + 3 = 0 [3 marks]

Section C
Question 5. State and prove Basic Proportionality Theorem. [5 marks]`);
  const [teacherNotes, setTeacherNotes] = useState<string>("");
  const [mySubmissions, setMySubmissions] = useState<any[]>([]);
  const [activeSubmission, setActiveSubmission] = useState<any>(null);
  const [teacherEditedContent, setTeacherEditedContent] = useState<string>("");
  const [showVersionModal, setShowVersionModal] = useState<boolean>(false);
  const [versionHistoryList, setVersionHistoryList] = useState<any[]>([]);

  // Phase 15 Enhancement: Admin Review States
  const [adminSubmissions, setAdminSubmissions] = useState<any[]>([]);
  const [adminCards, setAdminCards] = useState<any>(null);
  const [adminFilterStatus, setAdminFilterStatus] = useState<string>("");
  const [adminReviewModalSub, setActiveAdminReviewSub] = useState<any>(null);
  const [adminCommentInput, setAdminCommentInput] = useState<string>("");
  const [adminDestinationChoice, setAdminDestinationChoice] = useState<string>("QUESTION_BANK");
  const [adminVerifyAsOfficialPyq, setAdminVerifyAsOfficialPyq] = useState<boolean>(false);

  // Source Traceability Modal
  const [traceabilityModalQuestion, setTraceabilityModalQuestion] = useState<any>(null);

  // Builder Wizard States
  const [builderStep, setBuilderStep] = useState<number>(1);
  const [builderBoard, setBuilderBoard] = useState<string>("2");
  const [builderClass, setBuilderClass] = useState<string>("1");
  const [builderSubject, setBuilderSubject] = useState<string>("2");
  const [builderMedium, setBuilderMedium] = useState<string>("Hindi");
  const [builderPattern, setBuilderPattern] = useState<string>("");
  const [builderTitle, setBuilderTitle] = useState<string>("Class 10 Mathematics Board Practice Paper - 2026-27");
  const [builderMode, setBuilderMode] = useState<string>("automatic");

  // Initial Master Fetch
  useEffect(() => {
    fetch("/api/boards")
      .then(r => r.json())
      .then(d => { if (d.success && d.boards) setBoards(d.boards); })
      .catch(() => {});

    fetch("/api/classes")
      .then(r => r.json())
      .then(d => { if (d.success && d.classes) setClasses(d.classes); })
      .catch(() => {});

    fetch("/api/subjects")
      .then(r => r.json())
      .then(d => { if (d.success && d.subjects) setSubjects(d.subjects); })
      .catch(() => {});

    loadDashboardStats();
    // Load Phase 16 Math Symbols
    fetch("/api/math/symbols")
      .then(r => r.json())
      .then(d => { if (d.success) setMathSymbols(d.categories); });
  }, []);

  const loadDashboardStats = async () => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/api/exam-prep/dashboard-stats?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setDashboardStats(data.stats);

      const qRes = await fetch(`/api/exam-prep/quality-dashboard?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const qData = await qRes.json();
      if (qData.success) setQualityStats(qData);
    } catch (e) {}
  };

  const loadTeacherSubmissions = async () => {
    const token = getAuthToken();
    try {
      const res = await fetch("/api/exam-prep/submissions/my", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setMySubmissions(data.submissions);
    } catch (e) {}
  };

  const loadAdminSubmissions = async () => {
    const token = getAuthToken();
    try {
      let url = "/api/exam-prep/admin/submissions";
      if (adminFilterStatus) url += `?status=${adminFilterStatus}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setAdminSubmissions(data.submissions);
        setAdminCards(data.cards);
      }
    } catch (e) {}
  };

  // Tab Loaders
  useEffect(() => {
    const token = getAuthToken();
    const headers = { Authorization: `Bearer ${token}` };

    if (activeTab === "dashboard") {
      loadDashboardStats();
    } else if (activeTab === "teacher-submissions") {
      loadTeacherSubmissions();
    } else if (activeTab === "admin-submissions") {
      loadAdminSubmissions();
    } else if (activeTab === "extraction" || activeTab === "sources") {
      fetch(`/api/exam-prep/sources?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setSourceFiles(d.sources); });

      fetch(`/api/exam-prep/chapters?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setChapters(d.chapters); });

      fetch(`/api/exam-prep/quality-dashboard?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setQualityStats(d); });
    } else if (activeTab === "chapters") {
      fetch(`/api/exam-prep/content-stats?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setContentStats(d.chapters); });
    } else if (activeTab === "topics") {
      fetch(`/api/exam-prep/chapters?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.chapters.length > 0) {
            setChapters(d.chapters);
            const chapId = selectedChapter || d.chapters[0].id;
            fetch(`/api/exam-prep/topics?chapter_id=${chapId}`, { headers })
              .then(tr => tr.json())
              .then(td => { if (td.success) setTopics(td.topics); });
          }
        });
    } else if (activeTab === "questions") {
      let url = `/api/question-bank?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`;
      if (selectedMedium) url += `&medium=${selectedMedium}`;
      if (selectedChapter) url += `&chapter_id=${selectedChapter}`;
      if (selectedType) url += `&question_type=${selectedType}`;
      if (selectedDifficulty) url += `&difficulty=${selectedDifficulty}`;
      if (selectedVerification) url += `&verification_status=${selectedVerification}`;
      if (isPyqFilter) url += `&is_pyq=${isPyqFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      fetch(url, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setQuestions(d.questions); });

      fetch(`/api/exam-prep/chapters?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setChapters(d.chapters); });
    } else if (activeTab === "pyq") {
      let url = `/api/pyq?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`;
      if (selectedMedium) url += `&medium=${selectedMedium}`;
      if (selectedChapter) url += `&chapter_id=${selectedChapter}`;
      if (selectedYear) url += `&year=${selectedYear}`;
      if (selectedSet) url += `&set_code=${selectedSet}`;

      fetch(url, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setPyqs(d.pyqs); });

      fetch(`/api/exam-prep/chapters?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setChapters(d.chapters); });
    } else if (activeTab === "pyq-analysis") {
      fetch(`/api/exam-prep/pyq-analysis?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setPyqAnalysis(d); });
    } else if (activeTab === "papers") {
      fetch(`/api/generated-papers?board_id=${selectedBoard}&class_id=${selectedClass}&subject_id=${selectedSubject}`, { headers })
        .then(r => r.json())
        .then(d => { if (d.success) setGeneratedPapers(d.papers); });
    }
  }, [activeTab, selectedBoard, selectedClass, selectedSubject, selectedMedium, selectedChapter, selectedType, selectedDifficulty, selectedVerification, selectedYear, selectedSet, isPyqFilter, searchQuery, adminFilterStatus]);

  const handleRunNormalizer = async () => {
    if (!normalizerInput.trim()) return;
    setNormalizerLoading(true);
    try {
      const res = await fetch("/api/math/normalize-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: normalizerInput })
      });
      const data = await res.json();
      if (data.success) {
        setNormalizerResult(data.result);
      }
    } catch (e) {} finally {
      setNormalizerLoading(false);
    }
  };

  const insertSymbolIntoNormalizer = (sym: string) => {
    setNormalizerInput(prev => prev + " " + sym);
  };

  const toggleSolution = (qId: number) => {
    setShowSolutionMap(prev => ({ ...prev, [qId]: !prev[qId] }));
  };

  // Phase 15 Real Extraction Trigger
  const handleExtractPaper = async () => {
    if (!extractionRawText.trim()) return;
    setLoading(true);
    setCommitMessage("");
    const token = getAuthToken();

    try {
      const res = await fetch("/api/exam-prep/pyq/extract", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          raw_text: extractionRawText,
          filename: extractionFilename,
          board_id: Number(selectedBoard),
          class_id: Number(selectedClass),
          subject_id: Number(selectedSubject),
          exam_year: Number(extractionYear),
          set_code: extractionSet,
          paper_code: extractionPaperCode
        })
      });
      const data = await res.json();
      if (data.success) {
        setExtractedQuestions(data.questions);
        setExtractionSummary(data);
        setSelectedExtractedIndices(data.questions.map((_: any, i: number) => i));
      } else {
        alert(data.message || "Extraction failed.");
      }
    } catch (e) {
      alert("Network error during paper extraction.");
    } finally {
      setLoading(false);
    }
  };

  // Bulk Chapter Assignment in Staging
  const handleApplyBulkChapter = async () => {
    if (!bulkChapterSelection || selectedExtractedIndices.length === 0) return;
    const chap = chapters.find(c => String(c.id) === String(bulkChapterSelection));
    if (!chap) return;

    setExtractedQuestions(prev =>
      prev.map((q, idx) => {
        if (selectedExtractedIndices.includes(idx)) {
          return {
            ...q,
            chapter_id: chap.id,
            chapter_number: chap.chapter_number,
            chapter_name: chap.name,
            chapter_confidence: 1.0,
            mapping_status: "VERIFIED",
            validation_status: q.duplicate_status === "EXACT_DUPLICATE" ? "DUPLICATE" : "VALID"
          };
        }
        return q;
      })
    );
  };

  // Commit Extracted PYQs Transactionally
  const handleCommitExtractedPYQ = async () => {
    if (extractedQuestions.length === 0) return;
    setLoading(true);
    setCommitMessage("");
    const token = getAuthToken();

    try {
      const srcRes = await fetch("/api/exam-prep/sources/register", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          filename: extractionFilename,
          file_type: extractionFilename.endsWith(".pdf") ? "pdf" : "txt",
          board_id: Number(selectedBoard),
          class_id: Number(selectedClass),
          subject_id: Number(selectedSubject),
          exam_year: Number(extractionYear),
          set_code: extractionSet,
          paper_code: extractionPaperCode,
          medium: selectedMedium || "English",
          source_type: "OFFICIAL",
          notes: `Imported on ${new Date().toLocaleDateString()}`,
          raw_content: extractionRawText
        })
      });
      const srcData = await srcRes.json();
      const sourceFileId = srcData.source_file_id || srcData.source_file?.id || null;

      const commitRes = await fetch("/api/exam-prep/pyq/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_file_id: sourceFileId,
          questions: extractedQuestions,
          board_id: Number(selectedBoard),
          class_id: Number(selectedClass),
          subject_id: Number(selectedSubject),
          exam_year: Number(extractionYear),
          set_code: extractionSet,
          paper_code: extractionPaperCode
        })
      });
      const commitData = await commitRes.json();
      if (commitData.success) {
        setCommitMessage(`Success: Committed ${commitData.committed_count} PYQs to database with full traceability!`);
        loadDashboardStats();
      } else {
        setCommitMessage(`Error: ${commitData.message}`);
      }
    } catch (e: any) {
      setCommitMessage("Transaction failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Phase 15 Enhancement: Teacher Create Submission
  const handleTeacherSubmitNew = async (saveAsDraft: boolean = false) => {
    if (!teacherRawContent.trim() || !teacherFilename.trim()) {
      alert("Filename and question paper content are required.");
      return;
    }
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch("/api/exam-prep/submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          content_type: teacherContentType,
          title: teacherTitle || `${teacherContentType} - ${teacherFilename}`,
          board_id: Number(selectedBoard),
          class_id: Number(selectedClass),
          subject_id: Number(selectedSubject),
          medium: selectedMedium || "English",
          original_filename: teacherFilename,
          file_type: teacherFileType,
          raw_content: teacherRawContent,
          teacher_notes: teacherNotes,
          save_as_draft: saveAsDraft
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(saveAsDraft ? "Submission draft saved!" : "Submission uploaded successfully!");
        loadTeacherSubmissions();
        // Automatically trigger AI process
        handleTeacherProcessSubmission(data.id);
      } else {
        alert(data.message || "Failed to create submission.");
      }
    } catch (e) {
      alert("Network error creating submission.");
    } finally {
      setLoading(false);
    }
  };

  // Phase 15 Enhancement: Teacher Process Submission
  const handleTeacherProcessSubmission = async (subId: number) => {
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`/api/exam-prep/submissions/${subId}/process`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        loadTeacherSubmissions();
        handleViewSubmissionDetail(subId);
      } else {
        alert(data.message || "Processing failed.");
      }
    } catch (e) {
      alert("Error processing submission.");
    } finally {
      setLoading(false);
    }
  };

  // View Submission Detail & Open Review Workspace
  const handleViewSubmissionDetail = async (subId: number) => {
    setLoading(true);
    const token = getAuthToken();
    try {
      const res = await fetch(`/api/exam-prep/submissions/${subId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setActiveSubmission(data.submission);
        setTeacherEditedContent(data.submission.corrected_content || data.submission.original_content);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  // Teacher Save Edits
  const handleTeacherSaveEdits = async () => {
    if (!activeSubmission) return;
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`/api/exam-prep/submissions/${activeSubmission.id}/teacher-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          corrected_content: teacherEditedContent,
          extracted_questions: activeSubmission.extracted_questions,
          teacher_notes: teacherNotes
        })
      });
      const data = await res.json();
      if (data.success) {
        alert("Edits saved to Version 3: Teacher Edited.");
        handleViewSubmissionDetail(activeSubmission.id);
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  // Teacher Final Submit to Admin
  const handleTeacherFinalSubmitToAdmin = async () => {
    if (!activeSubmission) return;
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`/api/exam-prep/submissions/${activeSubmission.id}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ teacher_notes: teacherNotes })
      });
      const data = await res.json();
      if (data.success) {
        alert("Submission sent to Administrator for official review!");
        handleViewSubmissionDetail(activeSubmission.id);
        loadTeacherSubmissions();
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  // Admin Open Review Detail
  const handleAdminOpenReview = async (subId: number) => {
    setLoading(true);
    const token = getAuthToken();
    try {
      const res = await fetch(`/api/exam-prep/admin/submissions/${subId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setActiveAdminReviewSub(data.submission);
        setAdminCommentInput(data.submission.admin_review_comments || "");
        setAdminDestinationChoice(data.submission.content_type === "PYQ" ? "PYQ_DATABASE" : "QUESTION_BANK");
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  // Admin Approve Submission
  const handleAdminApprove = async () => {
    if (!adminReviewModalSub) return;
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`/api/exam-prep/admin/submissions/${adminReviewModalSub.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          approved_destination: adminDestinationChoice,
          verify_as_official_pyq: adminVerifyAsOfficialPyq,
          admin_comments: adminCommentInput
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        setActiveAdminReviewSub(null);
        loadAdminSubmissions();
        loadDashboardStats();
      } else {
        alert(data.message || "Approval failed.");
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  // Admin Request Revision
  const handleAdminRequestRevision = async () => {
    if (!adminReviewModalSub) return;
    if (!adminCommentInput.trim()) {
      alert("Please provide review comments explaining the requested revisions.");
      return;
    }
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`/api/exam-prep/admin/submissions/${adminReviewModalSub.id}/request-revision`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ admin_comments: adminCommentInput })
      });
      const data = await res.json();
      if (data.success) {
        alert("Submission sent back to teacher with revision notes.");
        setActiveAdminReviewSub(null);
        loadAdminSubmissions();
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  // Admin Reject Submission
  const handleAdminReject = async () => {
    if (!adminReviewModalSub) return;
    setLoading(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`/api/exam-prep/admin/submissions/${adminReviewModalSub.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ admin_comments: adminCommentInput })
      });
      const data = await res.json();
      if (data.success) {
        alert("Submission rejected.");
        setActiveAdminReviewSub(null);
        loadAdminSubmissions();
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-blue-950 via-slate-900 to-indigo-950 rounded-2xl p-6 text-white shadow-xl border border-blue-800/40">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-widest">
              <ShieldCheck className="w-4 h-4" /> District School Education Department, Nuh
            </div>
            <h1 className="text-2xl font-black tracking-tight mt-1 flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-amber-400" /> Real Mathematics PYQ & Teacher Submission Studio
            </h1>
            <p className="text-xs text-blue-200 mt-1 max-w-2xl">
              HBSE & CBSE Class 10/12 • Multi-Format Teacher Content Upload (PDF, Word, Images, Excel) • AI Math Formatting • Admin Approval & Revision Trail • 100% Traceability.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveTab("teacher-submissions")}
              className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white rounded-xl text-xs font-black shadow-lg flex items-center gap-2 transition"
            >
              <Send className="w-4 h-4" /> Teacher Content Upload
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab("admin-submissions")}
                className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-slate-950 rounded-xl text-xs font-black shadow-lg flex items-center gap-2 transition"
              >
                <Inbox className="w-4 h-4" /> Admin Review Center
              </button>
            )}
          </div>
        </div>

        {/* Sub-Navigation Tabs */}
        <div className="flex items-center gap-2 mt-6 overflow-x-auto pt-2 border-t border-blue-800/60">
          {[
            { id: "dashboard", label: "Overview & Quality", icon: Layers },
            { id: "teacher-submissions", label: "Teacher Submissions", icon: Send },
            ...(isAdmin ? [{ id: "admin-submissions", label: "Admin Review Center", icon: Inbox }] : []),
            { id: "math-studio", label: "Math Studio & Normalizer", icon: Wand2 },
            { id: "extraction", label: "Paper Extraction Studio", icon: Upload },
            { id: "sources", label: "Source Files Registry", icon: Hash },
            { id: "chapters", label: "Syllabus & Chapters", icon: BookOpen },
            { id: "questions", label: "Question Bank", icon: FileCheck2 },
            { id: "pyq", label: "PYQ Database", icon: Award },
            { id: "pyq-analysis", label: "PYQ Analysis", icon: BarChart3 },
            { id: "builder", label: "Paper Builder", icon: Sparkles },
            { id: "papers", label: "Generated Papers", icon: GraduationCap }
          ].map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition whitespace-nowrap ${
                  active
                    ? "bg-amber-500 text-slate-950 font-black shadow-md"
                    : "bg-blue-900/40 text-blue-100 hover:bg-blue-800/60 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Global Filter Toolbar */}
      {activeTab !== "dashboard" && activeTab !== "teacher-submissions" && activeTab !== "admin-submissions" && (
        <div className="bg-white p-4 rounded-2xl shadow-xs border border-slate-200 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
              <Filter className="w-4 h-4 text-blue-700" /> Scope:
            </div>

            <select
              value={selectedBoard}
              onChange={e => setSelectedBoard(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
            >
              <option value="1">HBSE (Haryana Board)</option>
              <option value="2">CBSE (Central Board)</option>
            </select>

            <select
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
            >
              <option value="3">Class 6</option>
              <option value="4">Class 7</option>
              <option value="5">Class 8</option>
              <option value="6">Class 9</option>
              <option value="1">Class 10</option>
              <option value="7">Class 11</option>
              <option value="2">Class 12</option>
            </select>

            <select
              value={selectedSubject}
              onChange={e => setSelectedSubject(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
            >
              <option value="2">Mathematics</option>
            </select>

            <select
              value={selectedMedium}
              onChange={e => setSelectedMedium(e.target.value)}
              className="px-3 py-1.5 bg-purple-50 border border-purple-300 rounded-lg text-xs font-bold text-purple-950"
            >
              <option value="">All Mediums (Hindi/English/Bilingual)</option>
              <option value="Hindi">Hindi Medium (हिंदी)</option>
              <option value="English">English Medium</option>
              <option value="Bilingual">Bilingual (Hindi + English)</option>
            </select>
          </div>
        </div>
      )}

      {/* ----------------- VIEW: PHASE 16 MATH STUDIO & NORMALIZER ----------------- */}
      {activeTab === "math-studio" && (
        <div className="space-y-6">
          {/* Symbol Palette Toolbar */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-indigo-700" />
                <h2 className="text-sm font-black text-slate-900">Mathematics Symbol Palette (Click to Insert)</h2>
              </div>
              <span className="text-xs text-slate-500 font-medium">60 Standard Board Math Symbols</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(mathSymbols).map(([cat, syms]) => (
                <div key={cat} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <div className="text-[11px] font-black text-slate-700 uppercase tracking-wider">{cat}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {syms.map(s => (
                      <button
                        key={s.id || s.symbol}
                        onClick={() => insertSymbolIntoNormalizer(s.symbol)}
                        title={s.name + " (" + s.latex_code + ")"}
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-indigo-50 hover:border-indigo-400 border border-slate-200 rounded-lg text-sm font-bold text-slate-800 transition shadow-2xs hover:scale-105"
                      >
                        {s.symbol}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Side-by-Side Normalizer Sandbox */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Raw Input Box */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-black text-slate-900">
                  <Edit3 className="w-4 h-4 text-purple-700" /> Raw Mathematical Text / Spoken Expressions
                </div>
                <button
                  onClick={handleRunNormalizer}
                  disabled={normalizerLoading}
                  className="px-3.5 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white rounded-xl text-xs font-black shadow-sm flex items-center gap-1.5 transition"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  {normalizerLoading ? "Normalizing..." : "Normalize Math"}
                </button>
              </div>

              <textarea
                value={normalizerInput}
                onChange={e => setNormalizerInput(e.target.value)}
                rows={5}
                className="w-full p-3 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono focus:ring-2 focus:ring-purple-500 focus:outline-hidden"
                placeholder="Type or paste mathematical expressions here..."
              />

              <div className="text-[11px] text-slate-500 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                Auto-normalizes spoken math: <code>under root 2</code> → <code>√2</code>, <code>x square</code> → <code>x²</code>, <code>greater than equal to</code> → <code>≥</code>
              </div>
            </div>

            {/* Normalized & Rendered Output */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-black text-slate-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700" /> Normalized Output & Math Notation
                </div>
                {normalizerResult && (
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                    normalizerResult.classification === "GREEN"
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                      : normalizerResult.classification === "YELLOW"
                      ? "bg-amber-100 text-amber-800 border border-amber-300"
                      : "bg-red-100 text-red-800 border border-red-300"
                  }`}>
                    Quality: {normalizerResult.quality_score}% • {normalizerResult.classification}
                  </span>
                )}
              </div>

              <div className="p-4 bg-emerald-50/50 border border-emerald-200 rounded-xl min-h-[110px] text-xs font-mono text-slate-900 select-all">
                {normalizerResult ? normalizerResult.normalized_text : "Click 'Normalize Math' to inspect formatted expression."}
              </div>

              {normalizerResult && normalizerResult.changes_made?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[11px] font-bold text-slate-700">Modifications Applied ({normalizerResult.changes_made.length}):</div>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {normalizerResult.changes_made.map((ch: any, i: number) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-[10px] font-mono text-slate-700">
                        {ch.original} → <b>{ch.replacement}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ----------------- VIEW 1: DASHBOARD & QUALITY METRICS ----------------- */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {qualityStats && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div>
                  <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                    <Percent className="w-5 h-5 text-emerald-600" /> PYQ Database Content Quality & Traceability Dashboard
                  </h3>
                  <p className="text-xs text-slate-500">
                    Real-time indicators showing source completeness, chapter & topic mapping accuracy, and verification compliance.
                  </p>
                </div>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-950 border border-emerald-300 rounded-lg text-xs font-black">
                  Zero Fake Content Guarantee
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border">
                  <div className="text-xs text-slate-500 font-semibold">Total Registered Sources</div>
                  <div className="text-xl font-black text-slate-900 mt-1">{qualityStats.total_sources} Files</div>
                  <div className="text-[10px] text-blue-700 mt-1">SHA-256 Checksum Verified</div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border">
                  <div className="text-xs text-slate-500 font-semibold">Chapter Mapped Rate</div>
                  <div className="text-xl font-black text-emerald-700 mt-1">{qualityStats.chapter_mapped_pct}%</div>
                  <div className="text-[10px] text-slate-600 mt-1">{qualityStats.chapter_mapped_count} / {qualityStats.total_pyqs} PYQs</div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border">
                  <div className="text-xs text-slate-500 font-semibold">Topic Mapped Rate</div>
                  <div className="text-xl font-black text-purple-700 mt-1">{qualityStats.topic_mapped_pct}%</div>
                  <div className="text-[10px] text-slate-600 mt-1">{qualityStats.topic_mapped_count} / {qualityStats.total_pyqs} PYQs</div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border">
                  <div className="text-xs text-slate-500 font-semibold">Official Source Verified</div>
                  <div className="text-xl font-black text-blue-900 mt-1">{qualityStats.verified_pct}%</div>
                  <div className="text-[10px] text-slate-600 mt-1">{qualityStats.verified_pyqs_count} Verified</div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Action Navigation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              onClick={() => setActiveTab("teacher-submissions")}
              className="p-5 bg-white rounded-2xl border-2 border-purple-200 hover:border-purple-500 shadow-xs cursor-pointer transition space-y-2"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-800 flex items-center justify-center font-bold">
                <Send className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-black text-slate-900">Teacher Content Upload</h4>
              <p className="text-xs text-slate-500">
                Submit question papers, notes, worksheets, and solutions in any format (PDF, Word, Images, Excel).
              </p>
            </div>

            {isAdmin && (
              <div
                onClick={() => setActiveTab("admin-submissions")}
                className="p-5 bg-white rounded-2xl border-2 border-amber-200 hover:border-amber-500 shadow-xs cursor-pointer transition space-y-2"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-900 flex items-center justify-center font-bold">
                  <Inbox className="w-5 h-5" />
                </div>
                <h4 className="text-sm font-black text-slate-900">Admin Review Center</h4>
                <p className="text-xs text-slate-500">
                  Inspect teacher submissions, send revision notes, and approve verified content to the Question Bank.
                </p>
              </div>
            )}

            <div
              onClick={() => setActiveTab("extraction")}
              className="p-5 bg-white rounded-2xl border-2 border-emerald-200 hover:border-emerald-500 shadow-xs cursor-pointer transition space-y-2"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
                <Upload className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-black text-slate-900">Paper Extraction Pipeline</h4>
              <p className="text-xs text-slate-500">
                Segment raw question paper text into individual questions with section, marks, and auto-mapping.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ----------------- VIEW 2: TEACHER CONTENT SUBMISSIONS STUDIO ----------------- */}
      {activeTab === "teacher-submissions" && (
        <div className="space-y-6">
          {/* Submission Form Card */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="border-b pb-3 flex items-center justify-between">
              <div>
                <h2 className="text-base font-black text-slate-900 flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-purple-700" /> Mathematics Teacher Content Submission Studio
                </h2>
                <p className="text-xs text-slate-500">
                  Submit Question Papers, Worksheets, Solutions, or PYQ drafts. Supports PDF, DOCX, XLSX, Images, and Text.
                </p>
              </div>
              <span className="px-3 py-1 bg-purple-50 text-purple-900 border border-purple-200 rounded-lg text-xs font-bold">
                Multi-Format Ingestion
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Content Type</label>
                <select
                  value={teacherContentType}
                  onChange={e => setTeacherContentType(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-semibold"
                >
                  <option value="Question Paper">Question Paper</option>
                  <option value="Question Bank">Question Bank</option>
                  <option value="PYQ">Previous Year Question (PYQ)</option>
                  <option value="Chapter Content">Chapter Content</option>
                  <option value="Topic Content">Topic Content</option>
                  <option value="Individual Questions">Individual Questions</option>
                  <option value="Answer Key">Answer Key</option>
                  <option value="Solution">Solution</option>
                  <option value="Marking Scheme">Marking Scheme</option>
                  <option value="Practice Worksheet">Practice Worksheet</option>
                  <option value="Study Material">Study Material</option>
                  <option value="Other">Other Mathematics Content</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">File Format / Type</label>
                <select
                  value={teacherFileType}
                  onChange={e => setTeacherFileType(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-semibold"
                >
                  <option value="docx">Word Document (.docx)</option>
                  <option value="pdf">PDF Document (.pdf)</option>
                  <option value="image">Scanned Image (.jpg, .png)</option>
                  <option value="xlsx">Excel Spreadsheet (.xlsx)</option>
                  <option value="csv">CSV File (.csv)</option>
                  <option value="txt">Plain Text (.txt)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">File Name</label>
                <input
                  type="text"
                  value={teacherFilename}
                  onChange={e => setTeacherFilename(e.target.value)}
                  className="w-full p-2 bg-slate-50 border rounded-lg font-semibold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Submission Title (Optional)</label>
                <input
                  type="text"
                  value={teacherTitle}
                  onChange={e => setTeacherTitle(e.target.value)}
                  placeholder="Class 10 Unit Test Paper"
                  className="w-full p-2 bg-slate-50 border rounded-lg font-semibold"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Question Paper / Content Text (or OCR transcribed content):
              </label>
              <textarea
                rows={7}
                value={teacherRawContent}
                onChange={e => setTeacherRawContent(e.target.value)}
                placeholder="Section A\nQuestion 1. Find the HCF...\nQuestion 2. If alpha and beta..."
                className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-medium text-slate-900 focus:bg-white"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="text-[11px] text-slate-500 font-medium">
                Automatic SHA-256 Checksum generated. Version 1 will be preserved.
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTeacherSubmitNew(true)}
                  disabled={loading}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition border"
                >
                  Save as Draft
                </button>
                <button
                  onClick={() => handleTeacherSubmitNew(false)}
                  disabled={loading}
                  className="px-6 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-xl text-xs font-black shadow-md flex items-center gap-1.5"
                >
                  <Wand2 className="w-4 h-4" /> Upload & Process with Math AI
                </button>
              </div>
            </div>
          </div>

          {/* Active Workspace / Preview Card */}
          {activeSubmission && (
            <div className="bg-white p-6 rounded-2xl border-2 border-purple-300 shadow-md space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-purple-100 text-purple-900 font-black rounded-lg text-xs">
                      Submission #{activeSubmission.id}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-black uppercase ${
                      activeSubmission.status === "APPROVED" ? "bg-emerald-100 text-emerald-900" :
                      activeSubmission.status === "NEEDS_REVISION" ? "bg-amber-100 text-amber-900" :
                      activeSubmission.status === "SUBMITTED_TO_ADMIN" ? "bg-blue-100 text-blue-900" : "bg-slate-100 text-slate-900"
                    }`}>
                      Status: {activeSubmission.status.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs font-bold text-slate-600">
                      Quality Score: {activeSubmission.quality_score}%
                    </span>
                  </div>
                  <h3 className="text-sm font-black text-slate-900 mt-1">{activeSubmission.title}</h3>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setVersionHistoryList(activeSubmission.versions || []);
                      setShowVersionModal(true);
                    }}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold flex items-center gap-1 border"
                  >
                    <History className="w-3.5 h-3.5" /> Version History ({activeSubmission.versions?.length || 1})
                  </button>
                </div>
              </div>

              {/* Revision Alert If Returned By Admin */}
              {activeSubmission.status === "NEEDS_REVISION" && activeSubmission.admin_review_comments && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-black text-amber-900">
                    <AlertTriangle className="w-4 h-4 text-amber-700" /> Admin Revision Instructions:
                  </div>
                  <p className="text-xs text-amber-800 font-medium whitespace-pre-line">
                    "{activeSubmission.admin_review_comments}"
                  </p>
                  <p className="text-[11px] text-amber-700 mt-1">
                    Please modify the content below and click "Save & Resubmit to Admin".
                  </p>
                </div>
              )}

              {/* Side-by-Side Before vs After Workspace */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>Original Uploaded Content (Preserved):</span>
                    <span className="text-[10px] text-slate-400 font-mono">Read-Only</span>
                  </div>
                  <textarea
                    rows={8}
                    readOnly
                    value={activeSubmission.original_content || ""}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-600 cursor-not-allowed"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="text-xs font-bold text-slate-700 flex items-center justify-between">
                    <span>AI Mathematical Formatted / Editable Content:</span>
                    <span className="text-[10px] text-emerald-700 font-bold">Version 3 Candidate</span>
                  </div>
                  <textarea
                    rows={8}
                    value={teacherEditedContent}
                    onChange={e => setTeacherEditedContent(e.target.value)}
                    className="w-full p-3 bg-white border border-purple-300 focus:border-purple-600 rounded-xl text-xs font-mono font-medium text-slate-900"
                  />
                </div>
              </div>

              {/* Teacher Actions Bar */}
              <div className="flex items-center justify-between pt-2 border-t">
                <button
                  onClick={handleTeacherSaveEdits}
                  disabled={loading}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold border"
                >
                  Save Edits (Version 3)
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleTeacherFinalSubmitToAdmin}
                    disabled={loading}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-900 to-indigo-900 hover:from-blue-800 hover:to-indigo-800 text-amber-300 rounded-xl text-xs font-black shadow-md flex items-center gap-1.5"
                  >
                    <Send className="w-4 h-4" /> {activeSubmission.status === "NEEDS_REVISION" ? "Save & Resubmit to Admin" : "Submit to Admin for Review"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Teacher Submissions History List */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900">
                My Content Submissions ({mySubmissions.length})
              </h3>
              <button
                onClick={loadTeacherSubmissions}
                className="text-xs text-blue-700 font-bold flex items-center gap-1 hover:text-blue-900"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            <div className="divide-y divide-slate-100">
              {mySubmissions.map(sub => (
                <div key={sub.id} className="p-4 hover:bg-slate-50/80 transition flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-slate-900">{sub.title}</h4>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-900 rounded text-[10px] font-bold">
                        {sub.content_type}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                        sub.status === "APPROVED" ? "bg-emerald-100 text-emerald-900" :
                        sub.status === "NEEDS_REVISION" ? "bg-amber-100 text-amber-900" :
                        sub.status === "SUBMITTED_TO_ADMIN" ? "bg-blue-100 text-blue-900" : "bg-slate-100 text-slate-700"
                      }`}>
                        {sub.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      File: {sub.original_filename} ({sub.file_type.toUpperCase()}) • {sub.board_code} {sub.class_name} {sub.subject_name} • Score: {sub.quality_score}%
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleViewSubmissionDetail(sub.id)}
                      className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-900 font-bold rounded-lg text-xs border border-purple-200"
                    >
                      Open Studio
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ----------------- VIEW 3: ADMIN REVIEW DASHBOARD & CENTER ----------------- */}
      {activeTab === "admin-submissions" && isAdmin && (
        <div className="space-y-6">
          {/* Admin KPI Cards */}
          {adminCards && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div
                onClick={() => setAdminFilterStatus("SUBMITTED_TO_ADMIN")}
                className="p-4 bg-white rounded-2xl border border-blue-200 shadow-xs cursor-pointer hover:border-blue-500 transition"
              >
                <div className="text-xs text-slate-500 font-semibold">Pending Admin Review</div>
                <div className="text-2xl font-black text-blue-900 mt-1">{adminCards.pending_review}</div>
                <div className="text-[10px] text-blue-600 mt-0.5">Action Required</div>
              </div>

              <div
                onClick={() => setAdminFilterStatus("APPROVED")}
                className="p-4 bg-white rounded-2xl border border-emerald-200 shadow-xs cursor-pointer hover:border-emerald-500 transition"
              >
                <div className="text-xs text-slate-500 font-semibold">Approved Submissions</div>
                <div className="text-2xl font-black text-emerald-700 mt-1">{adminCards.approved}</div>
                <div className="text-[10px] text-emerald-600 mt-0.5">Committed to Database</div>
              </div>

              <div
                onClick={() => setAdminFilterStatus("NEEDS_REVISION")}
                className="p-4 bg-white rounded-2xl border border-amber-200 shadow-xs cursor-pointer hover:border-amber-500 transition"
              >
                <div className="text-xs text-slate-500 font-semibold">Needs Revision</div>
                <div className="text-2xl font-black text-amber-700 mt-1">{adminCards.needs_revision}</div>
                <div className="text-[10px] text-amber-600 mt-0.5">Returned to Teacher</div>
              </div>

              <div
                onClick={() => setAdminFilterStatus("")}
                className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs cursor-pointer hover:border-slate-400 transition"
              >
                <div className="text-xs text-slate-500 font-semibold">Total Submissions</div>
                <div className="text-2xl font-black text-slate-900 mt-1">{adminCards.total}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">All Time Records</div>
              </div>
            </div>
          )}

          {/* Submissions List Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900">
                Teacher Submissions Queue ({adminSubmissions.length})
              </h3>
              <div className="flex items-center gap-2">
                <select
                  value={adminFilterStatus}
                  onChange={e => setAdminFilterStatus(e.target.value)}
                  className="p-1 bg-white border rounded-lg text-xs font-semibold"
                >
                  <option value="">All Statuses</option>
                  <option value="SUBMITTED_TO_ADMIN">Submitted to Admin</option>
                  <option value="UNDER_ADMIN_REVIEW">Under Admin Review</option>
                  <option value="NEEDS_REVISION">Needs Revision</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <button
                  onClick={loadAdminSubmissions}
                  className="p-1 text-slate-600 hover:text-slate-900"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="divide-y divide-slate-100 text-xs">
              {adminSubmissions.map(sub => (
                <div key={sub.id} className="p-4 hover:bg-slate-50 flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900">{sub.title}</span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-900 rounded text-[10px] font-bold">
                        {sub.content_type}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                        sub.status === "APPROVED" ? "bg-emerald-100 text-emerald-900" :
                        sub.status === "NEEDS_REVISION" ? "bg-amber-100 text-amber-900" :
                        sub.status === "SUBMITTED_TO_ADMIN" ? "bg-blue-100 text-blue-900" : "bg-slate-100 text-slate-700"
                      }`}>
                        {sub.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="text-slate-500">
                      Teacher: <span className="font-bold text-slate-800">{sub.teacher_username}</span> • File: {sub.original_filename} • {sub.board_code} {sub.class_name} • Score: {sub.quality_score}%
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAdminOpenReview(sub.id)}
                      className="px-3.5 py-1.5 bg-blue-900 hover:bg-blue-950 text-white rounded-lg text-xs font-bold shadow-xs flex items-center gap-1"
                    >
                      <CheckSquare className="w-3.5 h-3.5" /> Review & Action
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ADMIN REVIEW INSPECTION MODAL */}
      {adminReviewModalSub && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-2 font-black text-slate-900 text-sm">
                <CheckSquare className="w-5 h-5 text-blue-700" /> Admin Content Inspection & Verification
              </div>
              <button
                onClick={() => setActiveAdminReviewSub(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 p-3 rounded-xl border">
                <div><span className="text-slate-500">Teacher:</span> <strong className="text-slate-900">{adminReviewModalSub.teacher_username}</strong></div>
                <div><span className="text-slate-500">Type:</span> <strong>{adminReviewModalSub.content_type}</strong></div>
                <div><span className="text-slate-500">Board/Class:</span> <strong>{adminReviewModalSub.board_code} {adminReviewModalSub.class_name}</strong></div>
                <div><span className="text-slate-500">Quality Score:</span> <strong className="text-emerald-700">{adminReviewModalSub.quality_score}%</strong></div>
              </div>

              {/* Side-by-Side Original vs Corrected */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="font-bold text-slate-700 mb-1">Original Uploaded File Content:</div>
                  <textarea
                    rows={6}
                    readOnly
                    value={adminReviewModalSub.original_content || ""}
                    className="w-full p-2.5 bg-slate-50 border rounded-lg font-mono text-[11px] text-slate-600"
                  />
                </div>

                <div>
                  <div className="font-bold text-slate-700 mb-1">AI Mathematical Corrected Content:</div>
                  <textarea
                    rows={6}
                    readOnly
                    value={adminReviewModalSub.corrected_content || ""}
                    className="w-full p-2.5 bg-white border border-emerald-300 rounded-lg font-mono text-[11px] text-slate-900"
                  />
                </div>
              </div>

              {/* Destination & Action Setup */}
              <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-200 space-y-2">
                <div className="font-black text-blue-950">Approval Destination & Verification Options:</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Target Destination</label>
                    <select
                      value={adminDestinationChoice}
                      onChange={e => setAdminDestinationChoice(e.target.value)}
                      className="w-full p-2 bg-white border rounded-lg font-semibold text-xs"
                    >
                      <option value="QUESTION_BANK">Verified Question Bank</option>
                      <option value="PYQ_DATABASE">PYQ Archive Database</option>
                      <option value="CHAPTER_MASTER">Curriculum Chapter Master</option>
                      <option value="ANSWER_KEY">Official Answer Key Repository</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2 pt-5">
                    <input
                      type="checkbox"
                      id="pyqVerifyCheck"
                      checked={adminVerifyAsOfficialPyq}
                      onChange={e => setAdminVerifyAsOfficialPyq(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="pyqVerifyCheck" className="text-slate-800 font-bold">
                      Designate as Official Board Verified PYQ
                    </label>
                  </div>
                </div>
              </div>

              {/* Review Comments */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Admin Comments / Revision Instructions (Mandatory if sending back):
                </label>
                <textarea
                  rows={2}
                  value={adminCommentInput}
                  onChange={e => setAdminCommentInput(e.target.value)}
                  placeholder="Enter comments or specific correction instructions for the teacher..."
                  className="w-full p-2.5 bg-slate-50 border rounded-lg text-xs"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-3 border-t">
              <button
                onClick={handleAdminReject}
                disabled={loading}
                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-800 font-bold rounded-xl text-xs border border-red-200"
              >
                Reject Submission
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleAdminRequestRevision}
                  disabled={loading}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl text-xs shadow-xs"
                >
                  Send Back for Revision
                </button>
                <button
                  onClick={handleAdminApprove}
                  disabled={loading}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs shadow-md flex items-center gap-1"
                >
                  <Check className="w-4 h-4" /> Approve & Commit Content
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VERSION HISTORY MODAL */}
      {showVersionModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-2 font-black text-slate-900 text-sm">
                <History className="w-4 h-4 text-purple-700" /> Submission Version History
              </div>
              <button
                onClick={() => setShowVersionModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              {versionHistoryList.map((ver, idx) => (
                <div key={ver.id || idx} className="p-3 bg-slate-50 rounded-xl border space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">{ver.version_label}</span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {new Date(ver.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-600 font-mono truncate max-w-md">
                    {ver.content?.slice(0, 100)}...
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setShowVersionModal(false)}
                className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
